import { useEffect, useState } from 'react';

const GAP = 8;
const SIDE = 12;
// The canvas menu and the parts palette are drawn at the top of the lab canvas; never cover them
const TOP_RESERVE = 140;
const MIN_HEIGHT = 48;
// Below this the cards start collapsed: both open cards (checklist + three starters) need about 340 px
const COMPACT_HEIGHT = 340;
const COMPACT_LAB_WIDTH = 700;
const POLL_MS = 500;

// The guest CTA (ActionMessage) is a position: fixed box around #action-message-button
const findCtaRect = () => {
  try {
    let node = document.getElementById('action-message-button');
    while (node && node !== document.body) {
      if (window.getComputedStyle(node).position === 'fixed') break;
      node = node.parentElement;
    }
    if (!node || node === document.body) return null;
    const rect = node.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 ? rect : null;
  } catch (e) {
    return null;
  }
};

/**
 * Where the first-session column goes: one column at the inline-end edge, starting just under the
 * canvas menu and parts palette. The guest CTA sits at the bottom of the screen, so a top anchor
 * keeps them apart; when they share a vertical band the column stops above the CTA. The column
 * stays within the outer 30% of the lab width so the canvas centre is left clear. On mobile, or
 * when the room left is small, the cards start collapsed.
 * Pure: takes rects (viewport coordinates), returns { width, top, maxHeight, compact }.
 */
export const computePlacement = ({ lab, cta, rtl, isMobile }) => {
  const labWidth = lab.width;
  const outerBand = Math.round(labWidth * 0.3) - SIDE;
  const width = Math.max(
    Math.min(isMobile ? 300 : Math.min(280, outerBand), labWidth - 2 * SIDE),
    0
  );
  const left = rtl ? lab.left + SIDE : lab.right - SIDE - width;
  const right = left + width;

  // Lowest point the column may reach: the lab's bottom margin, or the CTA's top when they meet
  let floor = lab.bottom - (isMobile ? 110 : 64);
  if (cta && cta.left < right + GAP && cta.right > left - GAP) floor = Math.min(floor, cta.top - GAP);

  const top = TOP_RESERVE;
  const maxHeight = Math.max(Math.round(floor - (lab.top + top)), MIN_HEIGHT);
  const compact = isMobile || maxHeight < COMPACT_HEIGHT || labWidth < COMPACT_LAB_WIDTH;
  return { width, top, maxHeight, compact };
};

const samePlacement = (a, b) => a && b && a.width === b.width && a.top === b.top &&
  a.maxHeight === b.maxHeight && a.compact === b.compact;

/** Re-measures while the column is shown (window size, zoom and the CTA's appearance all change it). */
const useLayerPlacement = ({ enabled, rtl, isMobile }) => {
  const [placement, setPlacement] = useState(null);

  useEffect(() => {
    if (!enabled) return undefined;
    const measure = () => {
      try {
        const labDiv = document.getElementById('lab-div-id');
        if (!labDiv) return;
        const lab = labDiv.getBoundingClientRect();
        if (lab.width <= 0 || lab.height <= 0) return;
        const next = computePlacement({ lab, cta: findCtaRect(), rtl, isMobile });
        setPlacement((current) => (samePlacement(current, next) ? current : next));
      } catch (e) {
        // Layout reads failing must never break the lab; keep the last placement
      }
    };
    measure();
    const handle = setInterval(measure, POLL_MS);
    window.addEventListener('resize', measure);
    return () => {
      clearInterval(handle);
      window.removeEventListener('resize', measure);
    };
  }, [enabled, rtl, isMobile]);

  return placement;
};

export default useLayerPlacement;
